// CategoryBrowser component for product category selection
// Column-view modal interface with lazy loading support
// Loads categories level-by-level as user navigates

import React, { useState, useCallback, useEffect } from "react";
import { useCategoryBrowser } from "../../../../app/modules/product/hooks/useCategoryData";
import { useCategoryName } from "../../../../app/modules/category/hooks/useCategoryName";
import {
  CategoryItem as ImportedCategoryItem,
  MarketplaceType,
} from "../../../../app/models/MarketplaceCategory";

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

export interface CategoryItem {
  id: string;
  name: string;
  nameLocalized?: {  // Localized category names for language switching
    en?: string;     // English name
    id?: string;     // Indonesian name
  };
  hasChildren?: boolean; // Optional for backward compatibility
  isActive?: boolean;
  parentCategoryId?: string;
  // Backward compatibility fields (for tree-based usage)
  level?: number;
  children?: CategoryItem[];
  path?: string | string[]; // Category path - can be string or array of path parts
}

export interface CategoryBrowserProps {
  /** Root categories (level 1) - new lazy loading approach */
  rootCategories?: CategoryItem[];
  /** Loading state for root categories */
  rootLoading?: boolean;
  /** Callback to load children for a category - new lazy loading approach */
  loadChildren?: (parentId: string) => Promise<CategoryItem[]>;
  /** Currently selected category path */
  selectedCategory?: CategoryItem[];
  /** Change handler when category is selected */
  onChange: (categoryPath: CategoryItem[]) => void;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close modal handler */
  onClose: () => void;
  /** Whether to allow empty selection */
  allowEmpty?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Component ID */
  id?: string;
  /** Test ID */
  "data-testid"?: string;
  // Backward compatibility props (tree-based approach)
  /** @deprecated Use rootCategories instead */
  categories?: CategoryItem[];
  /** @deprecated Use rootLoading instead */
  loading?: boolean;
}

interface LevelState {
  categories: CategoryItem[];
  loading: boolean;
  error: string | null;
}

const CategoryBrowser: React.FC<CategoryBrowserProps> = ({
  rootCategories: rootCategoriesProp,
  rootLoading: rootLoadingProp = false,
  loadChildren,
  selectedCategory = [],
  onChange,
  isOpen,
  onClose,
  allowEmpty = false,
  placeholder = "Select a category...",
  id,
  "data-testid": dataTestId,
  // Backward compatibility
  categories: categoriesProp,
  loading: loadingProp = false,
}) => {
  // Debug: Log props when they change
  useEffect(() => {
    console.log('[CategoryBrowser] Props changed:', {
      isOpen,
      selectedCategoryLength: selectedCategory.length,
      selectedCategory: selectedCategory.map(c => ({ id: c.id, name: c.name })),
      rootCategoriesCount: rootCategoriesProp?.length || 0
    });
  }, [isOpen, selectedCategory, rootCategoriesProp]);

  // Support both old and new props
  const isLegacyMode =
    !loadChildren && categoriesProp && categoriesProp.length > 0;
  const rootCategories =
    rootCategoriesProp ||
    (isLegacyMode
      ? categoriesProp.filter((c) => !c.parentCategoryId || c.level === 1)
      : []);
  const rootLoading = rootLoadingProp || loadingProp;

  // Get language-aware category name helper
  const { getCategoryName } = useCategoryName();

  // Selected category at each level
  const [selectedLevel1, setSelectedLevel1] = useState<CategoryItem | null>(
    null
  );
  const [selectedLevel2, setSelectedLevel2] = useState<CategoryItem | null>(
    null
  );
  const [selectedLevel3, setSelectedLevel3] = useState<CategoryItem | null>(
    null
  );
  const [selectedLevel4, setSelectedLevel4] = useState<CategoryItem | null>(
    null
  );

  // Children categories for each level (lazy loaded)
  const [level2, setLevel2] = useState<LevelState>({
    categories: [],
    loading: false,
    error: null,
  });
  const [level3, setLevel3] = useState<LevelState>({
    categories: [],
    loading: false,
    error: null,
  });
  const [level4, setLevel4] = useState<LevelState>({
    categories: [],
    loading: false,
    error: null,
  });

  // Load children for a category
  const loadChildrenForCategory = useCallback(
    async (
      parentId: string,
      setLevel: React.Dispatch<React.SetStateAction<LevelState>>
    ) => {
      // Legacy mode: find children from the tree structure
      if (isLegacyMode && categoriesProp) {
        const findChildrenInTree = (
          cats: CategoryItem[],
          targetId: string
        ): CategoryItem[] => {
          for (const cat of cats) {
            if (cat.id === targetId && cat.children) {
              return cat.children;
            }
            if (cat.children) {
              const found = findChildrenInTree(cat.children, targetId);
              if (found.length > 0) return found;
            }
          }
          return [];
        };
        const children = findChildrenInTree(categoriesProp, parentId);
        setLevel({ categories: children, loading: false, error: null });
        return;
      }

      // New lazy loading mode
      if (!loadChildren) {
        setLevel({
          categories: [],
          loading: false,
          error: "No loadChildren function provided",
        });
        return;
      }

      setLevel({ categories: [], loading: true, error: null });
      try {
        const children = await loadChildren(parentId);
        console.log('[CategoryBrowser] Loaded children for parentId:', parentId, 'count:', children.length);
        setLevel({ categories: children, loading: false, error: null });
      } catch (err) {
        console.error('[CategoryBrowser] Failed to load children for parentId:', parentId, err);
        setLevel({
          categories: [],
          loading: false,
          error: "Failed to load categories",
        });
      }
    },
    [loadChildren, isLegacyMode, categoriesProp]
  );

  // Helper to check if category has children (works in both modes)
  const categoryHasChildren = (cat: CategoryItem): boolean => {
    return (
      cat.hasChildren === true ||
      (cat.children !== undefined && cat.children.length > 0)
    );
  };

  // Debug: Log level states when they change
  useEffect(() => {
    console.log('[CategoryBrowser] Level states:', {
      level2Count: level2.categories.length,
      level2Loading: level2.loading,
      level3Count: level3.categories.length,
      level3Loading: level3.loading,
      level4Count: level4.categories.length,
      level4Loading: level4.loading,
      selectedLevel1: selectedLevel1?.id,
      selectedLevel2: selectedLevel2?.id,
      selectedLevel3: selectedLevel3?.id
    });
  }, [level2, level3, level4, selectedLevel1, selectedLevel2, selectedLevel3]);

  // Initialize from selectedCategory prop when modal opens
  useEffect(() => {
    // Only initialize when modal is actually open
    if (!isOpen) return;

    if (selectedCategory.length > 0) {
      console.log('[CategoryBrowser] Initializing from selectedCategory:', {
        pathLength: selectedCategory.length,
        path: selectedCategory.map(c => ({ id: c.id, name: c.name, hasChildren: c.hasChildren }))
      });

      // Set selected categories at each level
      setSelectedLevel1(selectedCategory[0] || null);
      setSelectedLevel2(selectedCategory[1] || null);
      setSelectedLevel3(selectedCategory[2] || null);
      setSelectedLevel4(selectedCategory[3] || null);

      // Load children for each level to show siblings in columns
      const level0HasChildren = categoryHasChildren(selectedCategory[0]);
      console.log('[CategoryBrowser] Level 0 check:', {
        id: selectedCategory[0]?.id,
        hasChildren: selectedCategory[0]?.hasChildren,
        children: selectedCategory[0]?.children,
        categoryHasChildren: level0HasChildren
      });
      if (selectedCategory[0] && level0HasChildren) {
        console.log('[CategoryBrowser] Loading children for level 1:', selectedCategory[0].id);
        loadChildrenForCategory(selectedCategory[0].id, setLevel2);
      }

      const level1HasChildren = selectedCategory[1] && categoryHasChildren(selectedCategory[1]);
      console.log('[CategoryBrowser] Level 1 check:', {
        id: selectedCategory[1]?.id,
        hasChildren: selectedCategory[1]?.hasChildren,
        children: selectedCategory[1]?.children,
        categoryHasChildren: level1HasChildren
      });
      if (level1HasChildren) {
        console.log('[CategoryBrowser] Loading children for level 2:', selectedCategory[1].id);
        loadChildrenForCategory(selectedCategory[1].id, setLevel3);
      }

      const level2HasChildren = selectedCategory[2] && categoryHasChildren(selectedCategory[2]);
      console.log('[CategoryBrowser] Level 2 check:', {
        id: selectedCategory[2]?.id,
        hasChildren: selectedCategory[2]?.hasChildren,
        children: selectedCategory[2]?.children,
        categoryHasChildren: level2HasChildren
      });
      if (level2HasChildren) {
        console.log('[CategoryBrowser] Loading children for level 3:', selectedCategory[2].id);
        loadChildrenForCategory(selectedCategory[2].id, setLevel4);
      }
    }
  }, [isOpen]);

  // Handle category selection at level 1
  const selectLevel1 = useCallback(
    (category: CategoryItem) => {
      console.log('[CategoryBrowser] selectLevel1 called:', category.id, 'hasChildren:', categoryHasChildren(category));
      setSelectedLevel1(category);
      setSelectedLevel2(null);
      setSelectedLevel3(null);
      setSelectedLevel4(null);
      setLevel3({ categories: [], loading: false, error: null });
      setLevel4({ categories: [], loading: false, error: null });

      if (categoryHasChildren(category)) {
        console.log('[CategoryBrowser] Loading children for selected level1:', category.id);
        loadChildrenForCategory(category.id, setLevel2);
      } else {
        setLevel2({ categories: [], loading: false, error: null });
      }
    },
    [loadChildrenForCategory, categoryHasChildren]
  );

  // Handle category selection at level 2
  const selectLevel2 = useCallback(
    (category: CategoryItem) => {
      setSelectedLevel2(category);
      setSelectedLevel3(null);
      setSelectedLevel4(null);
      setLevel4({ categories: [], loading: false, error: null });

      if (categoryHasChildren(category)) {
        loadChildrenForCategory(category.id, setLevel3);
      } else {
        setLevel3({ categories: [], loading: false, error: null });
      }
    },
    [loadChildrenForCategory]
  );

  // Handle category selection at level 3
  const selectLevel3 = useCallback(
    (category: CategoryItem) => {
      setSelectedLevel3(category);
      setSelectedLevel4(null);

      if (categoryHasChildren(category)) {
        loadChildrenForCategory(category.id, setLevel4);
      } else {
        setLevel4({ categories: [], loading: false, error: null });
      }
    },
    [loadChildrenForCategory]
  );

  // Handle category selection at level 4
  const selectLevel4 = useCallback((category: CategoryItem) => {
    setSelectedLevel4(category);
  }, []);

  // Build final path and call onChange
  const confirmSelection = useCallback(() => {
    const path: CategoryItem[] = [];
    if (selectedLevel1) path.push(selectedLevel1);
    if (selectedLevel2) path.push(selectedLevel2);
    if (selectedLevel3) path.push(selectedLevel3);
    if (selectedLevel4) path.push(selectedLevel4);

    console.log('[CategoryBrowser] confirmSelection called, path:', path.map(c => c.id));
    if (path.length > 0 || allowEmpty) {
      onChange(path);
      onClose();
    }
  }, [
    selectedLevel1,
    selectedLevel2,
    selectedLevel3,
    selectedLevel4,
    onChange,
    onClose,
    allowEmpty,
  ]);

  // Select a leaf category directly
  const selectFinal = useCallback(
    (category: CategoryItem, level: number) => {
      const path: CategoryItem[] = [];
      if (level >= 1 && selectedLevel1) path.push(selectedLevel1);
      if (level >= 2 && selectedLevel2) path.push(selectedLevel2);
      if (level >= 3 && selectedLevel3) path.push(selectedLevel3);
      path.push(category);

      console.log('[CategoryBrowser] selectFinal called for category:', category.id, 'level:', level, 'path:', path.map(c => c.id));
      onChange(path);
      onClose();
    },
    [selectedLevel1, selectedLevel2, selectedLevel3, onChange, onClose]
  );

  // Get current path display (uses language-aware names)
  const getCurrentPath = useCallback(() => {
    const parts = [];
    if (selectedLevel1) parts.push(getCategoryName(selectedLevel1));
    if (selectedLevel2) parts.push(getCategoryName(selectedLevel2));
    if (selectedLevel3) parts.push(getCategoryName(selectedLevel3));
    if (selectedLevel4) parts.push(getCategoryName(selectedLevel4));
    return parts.join(" > ") || placeholder;
  }, [
    selectedLevel1,
    selectedLevel2,
    selectedLevel3,
    selectedLevel4,
    placeholder,
    getCategoryName,
  ]);

  // Reset breadcrumb navigation
  const resetToLevel = useCallback((level: number) => {
    if (level === 0) {
      setSelectedLevel1(null);
      setSelectedLevel2(null);
      setSelectedLevel3(null);
      setSelectedLevel4(null);
      setLevel2({ categories: [], loading: false, error: null });
      setLevel3({ categories: [], loading: false, error: null });
      setLevel4({ categories: [], loading: false, error: null });
    } else if (level === 1) {
      setSelectedLevel2(null);
      setSelectedLevel3(null);
      setSelectedLevel4(null);
      setLevel3({ categories: [], loading: false, error: null });
      setLevel4({ categories: [], loading: false, error: null });
    } else if (level === 2) {
      setSelectedLevel3(null);
      setSelectedLevel4(null);
      setLevel4({ categories: [], loading: false, error: null });
    } else if (level === 3) {
      setSelectedLevel4(null);
    }
  }, []);

  if (!isOpen) return null;

  // Render a single category column
  const renderColumn = (
    title: string,
    categories: CategoryItem[],
    loading: boolean,
    selectedId: string | null,
    onSelect: (cat: CategoryItem) => void,
    level: number
  ) => (
    <div className="category-column">
      <div className="tw-p-3 tw-border-b tw-border-gray-200 dark:tw-border-[#233648]">
        <h4 className="tw-font-medium tw-text-gray-900 dark:tw-text-white tw-text-sm">
          {title}
        </h4>
      </div>
      <div className="tw-space-y-0.5 tw-p-2">
        {loading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-4">
            <div className="tw-animate-spin tw-h-5 tw-w-5 tw-border-2 tw-border-primary tw-border-t-transparent tw-rounded-full" />
          </div>
        ) : categories.length === 0 ? (
          <div className="tw-text-sm tw-text-gray-500 tw-p-2">
            No categories
          </div>
        ) : (
          categories.map((cat) => (
            <div
              key={cat.id}
              className={classNames(
                "tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1.5 tw-rounded hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-group",
                selectedId === cat.id ? "tw-bg-primary/10" : ""
              )}
            >
              <button
                onClick={() => onSelect(cat)}
                className={classNames(
                  "tw-flex-1 tw-text-left tw-flex tw-items-center tw-justify-between tw-min-w-0",
                  selectedId === cat.id
                    ? "tw-text-primary"
                    : "tw-text-gray-700 dark:tw-text-gray-300"
                )}
              >
                <span className="category-text tw-flex-1 tw-mr-2 tw-text-sm">
                  {getCategoryName(cat)}
                </span>
                {categoryHasChildren(cat) && (
                  <span className="material-symbols-outlined tw-text-gray-400 group-hover:tw-text-gray-600 tw-flex-shrink-0">
                    chevron_right
                  </span>
                )}
              </button>
              {!categoryHasChildren(cat) && (
                <button
                  onClick={() => selectFinal(cat, level)}
                  className="tw-ml-2 tw-px-2 tw-py-0.5 tw-bg-primary tw-text-white tw-text-xs tw-rounded hover:tw-bg-primary/90 tw-transition-colors tw-flex-shrink-0"
                >
                  Select
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div
      className="tw-fixed tw-inset-0 tw-z-[150] tw-flex tw-items-center tw-justify-center tw-p-2 sm:tw-p-4"
      role="dialog"
      id={id}
      data-testid={dataTestId}
    >
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-gray-900/60 tw-backdrop-blur-sm tw-transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="tw-relative tw-w-full tw-max-w-4xl tw-h-[85vh] sm:tw-h-[75vh] md:tw-h-[70vh] tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-shadow-2xl tw-flex tw-flex-col tw-overflow-hidden tw-ring-1 tw-ring-black/5">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-bg-white dark:tw-bg-[#1C252E]">
          <div>
            <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
              Select Category
            </h3>
            <p className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
              Browse categories in column view to find the right category.
            </p>
          </div>
          <button
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-500 dark:hover:tw-text-gray-300 tw-transition-colors tw-rounded-full tw-p-1 hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648]"
          >
            <span className="material-symbols-outlined tw-text-[24px]">
              close
            </span>
          </button>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="tw-px-4 sm:tw-px-6 tw-py-2 sm:tw-py-3 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-bg-gray-50 dark:tw-bg-[#151f28]">
          <div className="tw-flex tw-items-center tw-text-sm tw-text-gray-600 dark:tw-text-gray-300 tw-flex-wrap tw-gap-1">
            <span className="material-symbols-outlined tw-text-base tw-mr-2">
              category
            </span>
            {!selectedLevel1 ? (
              <span className="tw-text-gray-400 dark:tw-text-gray-500">
                Select a category...
              </span>
            ) : (
              <>
                <button
                  onClick={() => resetToLevel(0)}
                  className="tw-text-primary hover:tw-text-primary/80 tw-font-medium"
                >
                  {getCategoryName(selectedLevel1)}
                </button>
                {selectedLevel2 && (
                  <>
                    <span className="material-symbols-outlined tw-text-gray-400 tw-text-base">
                      chevron_right
                    </span>
                    <button
                      onClick={() => resetToLevel(1)}
                      className="tw-text-primary hover:tw-text-primary/80 tw-font-medium"
                    >
                      {getCategoryName(selectedLevel2)}
                    </button>
                  </>
                )}
                {selectedLevel3 && (
                  <>
                    <span className="material-symbols-outlined tw-text-gray-400 tw-text-base">
                      chevron_right
                    </span>
                    <button
                      onClick={() => resetToLevel(2)}
                      className="tw-text-primary hover:tw-text-primary/80 tw-font-medium"
                    >
                      {getCategoryName(selectedLevel3)}
                    </button>
                  </>
                )}
                {selectedLevel4 && (
                  <>
                    <span className="material-symbols-outlined tw-text-gray-400 tw-text-base">
                      chevron_right
                    </span>
                    <span className="tw-text-gray-800 dark:tw-text-gray-200 tw-font-medium">
                      {getCategoryName(selectedLevel4)}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Category Columns */}
        <div className="tw-flex-1 tw-flex tw-overflow-hidden">
          <div className="category-columns-container">
            {renderColumn(
              "Categories",
              rootCategories,
              rootLoading,
              selectedLevel1?.id || null,
              selectLevel1,
              1
            )}
            {selectedLevel1 &&
              categoryHasChildren(selectedLevel1) &&
              renderColumn(
                getCategoryName(selectedLevel1),
                level2.categories,
                level2.loading,
                selectedLevel2?.id || null,
                selectLevel2,
                2
              )}
            {selectedLevel2 &&
              categoryHasChildren(selectedLevel2) &&
              renderColumn(
                getCategoryName(selectedLevel2),
                level3.categories,
                level3.loading,
                selectedLevel3?.id || null,
                selectLevel3,
                3
              )}
            {selectedLevel3 &&
              categoryHasChildren(selectedLevel3) &&
              renderColumn(
                getCategoryName(selectedLevel3),
                level4.categories,
                level4.loading,
                selectedLevel4?.id || null,
                selectLevel4,
                4
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="tw-px-4 sm:tw-px-6 tw-py-3 sm:tw-py-4 tw-border-t tw-border-gray-200 dark:tw-border-[#233648] tw-bg-gray-50 dark:tw-bg-[#151f28] tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-3 sm:tw-gap-0">
          <div className="tw-text-sm tw-text-gray-500 dark:tw-text-gray-400 tw-truncate tw-max-w-md">
            {getCurrentPath()}
          </div>
          <div className="tw-flex tw-gap-3">
            <button
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-text-gray-600 dark:tw-text-gray-300 hover:tw-text-gray-800 dark:hover:tw-text-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={confirmSelection}
              disabled={!selectedLevel1 && !allowEmpty}
              className="tw-px-4 tw-py-2 tw-bg-primary tw-text-white tw-rounded hover:tw-bg-primary/90 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
            >
              Select Category
            </button>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .category-columns-container {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          height: 100%;
          scrollbar-width: thin;
          scrollbar-color: #e2e8f0 transparent;
        }
        .category-columns-container::-webkit-scrollbar { height: 6px; }
        .category-columns-container::-webkit-scrollbar-track { background: transparent; }
        .category-columns-container::-webkit-scrollbar-thumb { background-color: #e2e8f0; border-radius: 3px; }
        .dark .category-columns-container::-webkit-scrollbar-thumb { background-color: #364152; }
        .category-column {
          flex: 0 0 250px;
          min-width: 250px;
          border-right: 1px solid #e2e8f0;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          scroll-snap-align: start;
        }
        .dark .category-column { border-color: #233648; }
        @media (max-width: 768px) { .category-column { flex: 0 0 220px; } }
        @media (max-width: 480px) { .category-column { flex: 0 0 200px; } }
        .category-text {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.3;
          max-width: 100%;
        }
      `}</style>
    </div>
  );
};

export default CategoryBrowser;
